import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import jstz from 'jstz';
import { Tooltip } from 'react-tippy';
import { useParams } from 'react-router-dom';
import { Flex, Text, Box } from 'rebass/styled-components';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { ProcedureSearch } from '../styles';
import { AccountList, ProcedureList } from '../LinkProcedure/styles';
import PreviewPanel from '../LinkProcedure/PreviewPanel';
import { ProcedureAction } from './styles';

import {
  Card,
  CardHeader,
  Tabs,
  Accordion,
  Loader,
  SlidePanel,
  SearchInput,
  Alert,
} from 'ui/patterns';
import {
  Button,
  Checkbox,
  StickyListWrap,
  StickyList,
  StickyListTitle,
  StickyListItem,
  Stack,
  DataTable,
  DataTableWrapper,
} from 'ui/components';

import {
  useDeleteProcedure,
  useGetAccountListForImportEmbedProcedures,
  useGetArchivedProcedureList,
  useGetGlobalProcedureList,
  useGetLinkedAccountsForGlobalProcedure,
  useProcedureReferences,
  useImportProcedures,
  useForceImportProcedures,
} from 'hooks';

import * as Icon from 'assets/icons';

import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from 'utils/showToast';
import { confirmAlert } from 'utils/confirm-alert';

import { ErrorModal, ResultModal } from 'types/modalTypes';
import {
  ProcedureDetailsType,
  linkImportAccountList,
  ProcedureDetailsInList,
} from 'types/procedureBuilder';
import {
  ImportProcedureParamType,
  ImportModalType,
} from './importProcedureTypes';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export default function ImportProcedure({
  showPanel,
  togglePanel: toggle,
}: ImportModalType): ReactElement {
  const existingAccountsRef =
    useRef() as React.MutableRefObject<HTMLDivElement>;

  const params = useParams<{
    accountId: string;
  }>();
  const accountId = parseInt(params.accountId);

  const [isVisible, setIsVisible] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [invisibleState, setInvisibleState] = useState(false);
  const [archiveSearch, setArchiveSearch] = useState('');
  const [opendAccountIds, setOpenedAccountIds] = useState<Array<number>>([]);
  const [allAccountsOpenend, setAllAccountsOpened] = useState<boolean>(false);
  const [selectedGlobalProcedures, setSelectedGlobal] = useState<Array<any>>(
    [],
  );
  const [selectedExistingProcedures, setSelectedExisting] = useState<
    Array<any>
  >([]);
  // Flag to check if all accounts in existing accounts are selected
  const [allAccountsSelected, setAllAccountsSelected] =
    useState<boolean>(false);
  const [selectedArchivedProcedures, setSelectedArchivedProcedures] = useState<
    Array<any>
  >([]);
  const [duplicateProcedures, setDuplicateProcedures] = useState<
    Array<any> | undefined
  >([]);
  const [isDuplicateModal, setIsDuplicateModal] = useState(false);
  const [selectedDuplicates, setSelectedDuplicates] = useState<
    Array<any> | undefined
  >([]);
  const [selectedSkips, setSelectedSkips] = useState<Array<any> | undefined>(
    [],
  );
  const [showPreview, setShowPreview] = useState<
    | {
        accountId: number;
        procedureId: number;
      }
    | undefined
  >();
  const [linkedAccounts, setLinkedAccounts] = useState<
    ProcedureDetailsType | undefined
  >();
  const [linkedAccountsSearch, setLinkedAccountsSearch] = useState<string>('');

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(showPanel);
    }, 10);
  }, [showPanel]);

  const { data: globalProcedureList, isLoading: isGlobalLoading } =
    useGetGlobalProcedureList(accountId);

  const {
    data: archivedProcedureList,
    isLoading: isGetArchivedProcedureListLoading,
  } = useGetArchivedProcedureList(accountId);

  const {
    data: ExistingAccountData,
    fetchNextPage: existingAccountsFetchMore,
    hasNextPage,
    isFetching,
    isLoading: isGetAccountListLoading,
  } = useGetAccountListForImportEmbedProcedures(accountId, searchKeyword);

  const { mutate: importProcedures, isLoading: isImportLoading } =
    useImportProcedures();

  const { mutate: deleteProcedure, isLoading: isDeleteLoading } =
    useDeleteProcedure();

  const {
    mutate: getProcedureReference,
    isLoading: isProcedureReferenceLoading,
  } = useProcedureReferences();

  const {
    mutate: duplicateImportedProcedure,
    isLoading: isDuplicateImportsLoading,
  } = useForceImportProcedures();

  const {
    data: linkedAccountsForGlobalProcedure,
    isLoading: isLinkedAccountsForGlobalProcedureLoading,
  } = useGetLinkedAccountsForGlobalProcedure({
    accountId: linkedAccounts ? linkedAccounts.AccountId : 0,
    procedureId: linkedAccounts ? linkedAccounts?.Id : 0,
  });

  const isLoading =
    isGetArchivedProcedureListLoading ||
    isImportLoading ||
    isGlobalLoading ||
    isDeleteLoading ||
    isProcedureReferenceLoading;

  const handleScroll = useCallback(
    (e: any) => {
      if (existingAccountsRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
            e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
            e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && existingAccountsFetchMore();
        }
      }
    },
    [existingAccountsFetchMore, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  function togglePanel() {
    setIsVisible(false);
    setTimeout(() => {
      toggle();
    }, 500);
  }

  const globalProcedures = useMemo(() => {
    return globalProcedureList?.Data?.filter((item: ProcedureDetailsType) =>
      item.Name.toLocaleLowerCase().includes(globalSearch.toLocaleLowerCase()),
    );
  }, [globalProcedureList?.Data, globalSearch]);

  const existingAccounts = useMemo(() => {
    if (ExistingAccountData) {
      return ExistingAccountData?.pages
        ?.map((dataItem: any) => dataItem.Data)
        .flat();
    } else return [];
  }, [ExistingAccountData]);

  const archivedProcedures = useMemo(() => {
    return archivedProcedureList?.Data?.filter((item: any) =>
      item?.Procedure?.Name?.toLocaleLowerCase()?.includes(
        archiveSearch.toLocaleLowerCase(),
      ),
    );
  }, [archivedProcedureList?.Data, archiveSearch]);

  const isAnyProcedureChecked = (account: linkImportAccountList) => {
    const selectedAccount = selectedExistingProcedures?.find(
      (item: any) => item.accountId === account?.AccountId,
    );
    const totalProcedures = account.ProcedureDetails?.length || 0;
    const selectedProceduresCount = selectedAccount?.procedures.length;
    return (
      selectedProceduresCount > 0 && selectedProceduresCount < totalProcedures
    );
  };

  //To clear out all selected procedures
  const resetSelectedProcedures = () => {
    setSelectedGlobal([]);
    setSelectedExisting([]);
    setSelectedArchivedProcedures([]);
    setOpenedAccountIds([]);
    setAllAccountsSelected(false);
  };

  //To clear out all searched values
  const clearSearches = () => {
    setGlobalSearch('');
    setSearchKeyword('');
    setArchiveSearch('');
  };

  //Clear import duplication panel
  const resetDuplicatePanel = () => {
    setIsDuplicateModal && setIsDuplicateModal(false);
    setSelectedDuplicates([]);
    setSelectedSkips([]);
    setInvisibleState(false);
  };

  function getAllSelectedProcedures() {
    const globalSelected = [...selectedGlobalProcedures];
    const archivedSelected = [...selectedArchivedProcedures];

    let selectedExisting: Array<ImportProcedureParamType> = [];
    if (allAccountsSelected) {
      existingAccounts?.forEach((item: linkImportAccountList) => {
        if (item.ProcedureDetails) {
          const procedures = item.ProcedureDetails.map(
            (procedureItem: ProcedureDetailsInList) => {
              return {
                AccountId: item.AccountId,
                AccountName: item.AccountName,
                ProcedureId: procedureItem.ProcedureId,
                ProcedureName: procedureItem.ProcedureName,
                IsGlobal: procedureItem.IsGlobal,
              };
            },
          );

          selectedExisting = selectedExisting.concat(procedures);
        }
      });
    } else {
      selectedExistingProcedures?.forEach((accountData: any) => {
        if (accountData.procedures && accountData.procedures.length > 0) {
          const procedures = accountData.procedures.map((proc: any) => ({
            AccountId: accountData.accountId,
            AccountName: accountData.accountNameAndNumber,
            ProcedureId: proc.ProcedureId,
            ProcedureName: proc.ProcedureName,
            IsGlobal: proc.IsGlobal,
          }));
          selectedExisting = selectedExisting.concat(procedures);
        }
      });
    }

    const allSelections = [
      ...globalSelected,
      ...selectedExisting,
      ...archivedSelected,
    ];
    return allSelections;
  }

  // To check if duplicate procedure names exist in the selected procedures
  function isDuplicateProcedureNameExist(): boolean {
    const allSelections = getAllSelectedProcedures();
    const seen = new Set<string>();

    for (const { ProcedureName } of allSelections) {
      const name = ProcedureName.trim().toLowerCase();
      if (seen.has(name)) return true;
      seen.add(name);
    }
    return false;
  }

  const globalProceduresSelectedList = selectedGlobalProcedures;
  const archiveProceduresSelectedList = selectedArchivedProcedures;
  const selectedAccounts = selectedExistingProcedures;

  //duplicate checkbox for each entry
  const handleDuplicate = (procedure: any) => {
    const selected = selectedDuplicates ? selectedDuplicates : [];
    const index =
      selected &&
      selected.findIndex(
        (item: any) => item.ProcedureId === procedure.ProcedureId,
      );
    if (index === -1) {
      procedure.IsCopy = true;
      const duplicate = [...selected, procedure];
      setSelectedDuplicates(duplicate);
    } else {
      procedure.IsCopy = null;
      const duplicate = selectedDuplicates?.filter(
        (item: any) => item.ProcedureId !== procedure.ProcedureId,
      );
      setSelectedDuplicates(duplicate);
    }
  };

  //skip checkbox for each entry
  const handleSkip = (procedure: any) => {
    const selected = selectedSkips ? selectedSkips : [];
    const index =
      selected &&
      selected.findIndex(
        (item: any) => item.ProcedureId === procedure.ProcedureId,
      );
    if (index === -1) {
      procedure.IsSkip = true;
      const skip = [...selected, procedure];
      setSelectedSkips(skip);
    } else {
      procedure.IsSkip = null;
      const skip = selectedSkips?.filter(
        (item: any) => item.ProcedureId !== procedure.ProcedureId,
      );
      setSelectedSkips(skip);
    }
  };

  //check if all rows are  selected to be duplicated
  const isAllDuplicateChecked = () => {
    const selectedData = duplicateProcedures?.filter((item: any) => {
      if (item.IsCopy === true) return item;
    });
    if (
      selectedData?.length ===
      (duplicateProcedures && duplicateProcedures.length > 0
        ? duplicateProcedures.length
        : null)
    )
      return true;
    else return false;
  };

  //select all to duplicate
  const handleSelectAllDuplicates = () => {
    let selected: any[] | undefined = [];
    const selectedState = isAllDuplicateChecked();
    if (!selectedState) {
      selected = duplicateProcedures?.map((item: any) => {
        item.IsCopy = true;
        return item;
      });
      setSelectedDuplicates(selected);
    } else {
      selected = duplicateProcedures?.map((item: any) => {
        item.IsCopy = false;
        return item;
      });
      setSelectedDuplicates([]);
    }
  };

  //check if all rows are selected to be skipped
  const isAllSkipChecked = () => {
    const selectedData = duplicateProcedures?.filter(
      (item: any) => item.IsSkip === true,
    );
    if (
      selectedData?.length ===
      (duplicateProcedures && duplicateProcedures.length > 0
        ? duplicateProcedures.length
        : null)
    )
      return true;
    else return false;
  };

  //select all to skip
  const handleSelectAllSkips = () => {
    let selected: any[] | undefined = [];
    const selectedState = isAllSkipChecked();
    if (!selectedState) {
      selected = duplicateProcedures?.map((item: any) => {
        item.IsSkip = true;
        return item;
      });
      setSelectedSkips(selected);
    } else {
      selected = duplicateProcedures?.map((item: any) => {
        item.IsSkip = false;
        return item;
      });
      setSelectedSkips([]);
    }
  };

  const getArchivedTime = (archivedDate: any) => {
    const timezone = jstz.determine();
    const ts = dayjs(archivedDate + 'Z');
    const x = ts.tz(timezone.name());
    // const timeValue = x.format('YYYY MMM, DD ddd hh:mm A z');
    const timeValue = x.format('MMM DD, YYYY [at] hh:mm A');
    return timeValue;
  };

  const allGlobalsSelected =
    globalProcedures &&
    globalProcedures?.length > 0 &&
    globalProcedures?.length <= selectedGlobalProcedures.length;

  const allArchivedSelected =
    archivedProcedures &&
    archivedProcedures?.length > 0 &&
    archivedProcedures?.length <= selectedArchivedProcedures.length;

  const linkedAccountsArray =
    linkedAccountsForGlobalProcedure &&
    linkedAccountsForGlobalProcedure.Data?.filter((linked: string) =>
      linked.toLowerCase().includes(linkedAccountsSearch.toLowerCase()),
    );

  // Global List - Select All
  const handleGlobalSelectAll = (e: any) => {
    if (allGlobalsSelected) {
      setSelectedGlobal([]);
    } else if (globalProcedures) {
      setSelectedGlobal(prevSelectedGlobalProcedures => [
        ...prevSelectedGlobalProcedures,
        ...globalProcedures
          .filter(
            (item: any) =>
              !prevSelectedGlobalProcedures.some(
                selectedItem => selectedItem.Id === item.Id,
              ),
          )
          .map(item => ({
            ProcedureId: item.Id,
            AccountId: item.AccountId,
            ProcedureName: item.Name,
            IsGlobal: item.IsGlobal,
          })),
      ]);
    }
  };

  // Archived List - Select All
  const handleArchiveSelectAll = () => {
    if (allArchivedSelected) {
      setSelectedArchivedProcedures([]);
    } else if (archivedProcedures) {
      setSelectedArchivedProcedures(prevSelectedArchivedProcedures => [
        ...prevSelectedArchivedProcedures,
        ...archivedProcedures
          .filter(
            (item: any) =>
              !prevSelectedArchivedProcedures.some(
                selectedItem => selectedItem.ProcedureId === item.ProcedureId,
              ),
          )
          .map(item => ({
            ProcedureId: item.ProcedureId,
            AccountId: item.AccountId,
            ProcedureName: item.Procedure.Name,
            IsGlobal: item.Procedure.IsGlobal,
            LastUpdatedBy: item.LastUpdatedBy,
            LastUpdatedOn: item.LastUpdatedOn,
          })),
      ]);
    }
  };

  // Global - Individual Checkbox
  const handleGlobalProcedureSelect = (isSelected: boolean, item: any) => {
    setSelectedGlobal(prevSelected => {
      if (isSelected) {
        return prevSelected.filter(
          procedure => procedure.ProcedureId !== item.Id,
        );
      } else {
        return [
          ...prevSelected,
          {
            ProcedureId: item.Id,
            AccountId: item.AccountId,
            ProcedureName: item.Name,
            IsGlobal: item.IsGlobal,
          },
        ];
      }
    });
  };

  // Archived - Individual Checkbox
  const handleArchiveProcedureSelect = (isSelected: any, item: any) => {
    if (isSelected) {
      setSelectedArchivedProcedures([
        ...selectedArchivedProcedures.filter(
          procedure => procedure.ProcedureId !== item.ProcedureId,
        ),
      ]);
    } else {
      setSelectedArchivedProcedures([
        ...selectedArchivedProcedures,
        {
          AccountId: item.AccountId,
          ProcedureId: item.ProcedureId,
          ProcedureName: item.Procedure.Name,
          IsGlobal: item.Procedure.IsGlobal,
          LastUpdatedBy: item.LastUpdatedBy,
          LastUpdatedOn: item.LastUpdatedOn,
        },
      ]);
    }
  };

  //Existing Accounts - Select All
  const handleExistingAccountSelectAll = () => {
    if (!allAccountsSelected) {
      const selected =
        existingAccounts?.map((item: linkImportAccountList) => ({
          accountId: item.AccountId,
          accountNameAndNumber: item.AccountNameAndNumber,
          procedures: item.ProcedureDetails,
        })) || [];
      setSelectedExisting([...selected]);
    } else {
      setSelectedExisting([]);
    }
    setAllAccountsSelected(prevState => !prevState);
  };

  //Existing Accounts - Select Account
  const handleExistingAccountSelect = (
    isAllSelected: boolean,
    account: any,
  ) => {
    // all accounts selected
    if (allAccountsSelected) {
      const selectedExisting =
        existingAccounts
          ?.filter(item => item.AccountId !== account.AccountId)
          .map(item => ({
            accountId: item.AccountId,
            accountNameAndNumber: item.AccountNameAndNumber,
            procedures: item.ProcedureDetails,
          })) || [];
      setSelectedExisting([...selectedExisting]);
      setAllAccountsSelected(false);
      return;
    }
    //all procedures of accounts are selected
    else if (isAllSelected) {
      setSelectedExisting(prevSelected =>
        prevSelected.filter(
          existingItem =>
            !existingItem.procedures.some((proc: any) =>
              account.ProcedureDetails.some(
                (item: any) => item.ProcedureId === proc.ProcedureId,
              ),
            ),
        ),
      );
      return;
    } else {
      let existingAccount = selectedExistingProcedures.find(
        value => value.accountId === account.AccountId,
      );

      //account already selected - updated only procedures
      if (existingAccount) {
        const updatedProcedures = account.ProcedureDetails;
        existingAccount = { ...existingAccount, procedures: updatedProcedures };
        setSelectedExisting(prevSelectedExisting => {
          const index = prevSelectedExisting.findIndex(
            account => account.accountId === existingAccount.accountId,
          );
          if (index !== -1) {
            const updated = [...prevSelectedExisting];
            updated[index] = existingAccount;
            return updated;
          }

          return prevSelectedExisting;
        });
      } else {
        // Add the account's procedures if it's not already selected
        const newSelected = [
          ...selectedExistingProcedures.filter(i => {
            const isExists = account?.ProcedureDetails?.find(
              (item: any) => i.procedures.ProcedureId === item.ProcedureId,
            );
            return !isExists;
          }),
        ];

        const accountPIDs = account?.ProcedureDetails?.map((item: any) => item);
        if (accountPIDs)
          setSelectedExisting([
            ...newSelected,
            {
              accountId: account.AccountId,
              accountNameAndNumber: account.AccountNameAndNumber,
              procedures: [...accountPIDs],
            },
          ]);
      }
    }
  };

  //Existing Accounts - Select Procedure
  const handleExistingProcedureSelect = (
    isSelected: boolean,
    accountProcedure: any,
    account: any,
  ) => {
    if (allAccountsSelected) {
      let selectedExisting: Array<any> = [];
      existingAccounts?.forEach((item: linkImportAccountList) => {
        if (item.ProcedureDetails) {
          const procedures = item?.ProcedureDetails?.map(
            (proc: ProcedureDetailsInList) => proc,
          );
          selectedExisting = [
            ...selectedExisting,
            {
              accountId: item.AccountId,
              accountNameAndNumber: item.AccountNameAndNumber,
              procedures: [
                ...procedures.filter(
                  (i: any) => i.ProcedureId !== accountProcedure.ProcedureId,
                ),
              ],
            },
          ];
        }
      });
      setSelectedExisting([...selectedExisting]);
      setAllAccountsSelected(false);
      return;
    } else if (isSelected) {
      const filteredValues = selectedExistingProcedures?.map(
        (procedureList: any) => {
          return {
            ...procedureList,
            procedures: procedureList.procedures.filter(
              (item: any) => item.ProcedureId !== accountProcedure.ProcedureId,
            ),
          };
        },
      );
      const updatedValues = filteredValues.filter(
        item => item.procedures.length !== 0,
      );

      setSelectedExisting(updatedValues);
    } else {
      const existingValue = [...selectedExistingProcedures];
      if (existingValue.length) {
        const existingAccount = existingValue.find(
          value => value.accountId === account.AccountId,
        );

        if (existingAccount) {
          existingAccount.procedures.push(accountProcedure);
        } else {
          existingValue.push({
            accountId: account.AccountId,
            accountNameAndNumber: account.AccountNameAndNumber,
            procedures: [accountProcedure],
          });
        }

        setSelectedExisting(existingValue);
      } else {
        setSelectedExisting([
          ...selectedExistingProcedures,
          {
            accountId: account.AccountId,
            accountNameAndNumber: account.AccountNameAndNumber,
            procedures: [accountProcedure],
          },
        ]);
      }
    }
  };

  //remove selected archive procedure from preview panel
  const handleRemoveArchiveProcedure = (id: any) => {
    setSelectedArchivedProcedures([
      ...selectedArchivedProcedures.filter(i => i.ProcedureId !== id),
    ]);
  };

  //remove selected global procedure from preview panel
  const handleRemoveGlobalProcedure = (id: any) => {
    setSelectedGlobal([
      ...selectedGlobalProcedures.filter(i => i.ProcedureId !== id),
    ]);
  };

  //remove selected existing account  procedure from preview panel
  const handleRemoveExistingAccountProcedure = (item: any) => {
    if (allAccountsSelected) {
      let selectedExisting: Array<any> = [];
      existingAccounts?.forEach((acItem: any) => {
        if (acItem.ProcedureDetails) {
          const procedures = acItem.ProcedureDetails.map((proc: any) => proc);
          selectedExisting = [
            ...selectedExisting,
            {
              accountId: acItem.AccountId,
              accountNameAndNumber: acItem.AccountNameAndNumber,
              procedures: [
                ...procedures.filter(
                  (i: any) => i.ProcedureId !== item.ProcedureId,
                ),
              ],
            },
          ];
        }
      });
      setSelectedExisting([...selectedExisting]);
      setAllAccountsSelected(false);
      return;
    } else {
      setSelectedExisting(prevState =>
        prevState
          .map(eachProcedure => ({
            ...eachProcedure,
            procedures: eachProcedure.procedures.filter(
              (procedure: any) => procedure.ProcedureId !== item.ProcedureId,
            ),
          }))
          .filter(item => item.procedures.length > 0),
      );
    }
  };

  // delete procedure from archived list
  const handleDeleteProcedure = (item: any) => {
    getProcedureReference(
      {
        procedureIds: [item?.ProcedureId],
        accountId: item.AccountId,
        isRemove: true,
      },
      {
        onSuccess: (mutationResult: any) => {
          if (mutationResult.Data.length === 0) {
            deleteProcedure(
              {
                procedureIds: [item.ProcedureId],
                accountId: item.AccountId,
              },
              {
                onSuccess: () => {
                  setSelectedArchivedProcedures([
                    ...selectedArchivedProcedures.filter(
                      i => i.ProcedureId !== item.ProcedureId,
                    ),
                  ]);
                },
              },
            );
          } else {
            if (!mutationResult.Data[0].includes('0000_POLICIESMENU.html')) {
              confirmAlert({
                message:
                  'This procedure has been linked to other procedures. Are you sure you want to delete the procedure?',
                buttons: [
                  {
                    label: 'Yes',
                    onClick: () => {
                      deleteProcedure(
                        {
                          procedureIds: [item?.ProcedureId],
                          accountId: item.AccountId,
                        },
                        {
                          onSuccess: () => {
                            setSelectedArchivedProcedures([
                              ...selectedArchivedProcedures.filter(
                                i => i.ProcedureId !== item?.ProcedureId,
                              ),
                            ]);
                          },
                        },
                      );
                    },
                    className: 'pr-btn-primary',
                  },
                  {
                    label: 'No',
                    onClick: () => {
                      //
                    },
                  },
                ],
              });
            }
          }
        },
        onError: (error: ErrorModal) => {
          showErrorToast({
            message: error.Errors[0].Message,
          });
        },
      },
    );
  };

  const importProcedureTabs = [
    {
      id: 'globalProcedures',
      label: 'Global',
      content: (
        <Flex flexDirection="column" height="100%">
          <ProcedureSearch>
            <Checkbox
              handleChange={e => handleGlobalSelectAll(e)}
              checked={allGlobalsSelected}
              label=" "
              name="selectAllGlobalForImport"
            />
            <div className="search-input">
              <Icon.Search />
              <input
                value={globalSearch}
                onChange={e => setGlobalSearch(e.target.value)}
                type="search"
                placeholder="Search procedure name or Id"
              />
            </div>
          </ProcedureSearch>
          <Box flex="1 0 auto" height="1px" overflow="auto">
            <ProcedureList>
              {globalProcedures &&
                globalProcedures.map((item: any) => {
                  const isSelected = selectedGlobalProcedures?.find(
                    i => i.ProcedureId === item.Id,
                  );
                  return (
                    <Flex key={`Global_${item?.AccountId}_${item?.Id}`}>
                      <Checkbox
                        handleChange={() =>
                          handleGlobalProcedureSelect(isSelected, item)
                        }
                        checked={!!isSelected}
                        key={`Global_${item?.AccountId}_${item?.Id}`}
                        label={item.Name}
                        title={item.Name}
                        name={`Global_${item?.AccountId}_${item?.Id}`}
                        className="pb-cb__import-procedure"
                      />

                      <ProcedureAction
                        onClick={() => {
                          setLinkedAccounts(item);
                        }}
                        title="View linked accounts"
                      >
                        <Icon.Globe
                          fill="var(--color-state-success)"
                          title="View linked accounts"
                        />
                      </ProcedureAction>
                    </Flex>
                  );
                })}
            </ProcedureList>
          </Box>
        </Flex>
      ),
    },
    {
      id: 'exsitingAccounts',
      label: 'Existing Accounts',
      content: (
        <Flex flexDirection="column" height="100%">
          <ProcedureSearch>
            <Checkbox
              handleChange={() => handleExistingAccountSelectAll()}
              checked={allAccountsSelected}
              label=" "
              name="selectAllExistingAccounts"
            />
            <div className="search-input">
              <Icon.Search />
              <input
                onChange={e => {
                  setSearchKeyword(e.target.value);
                  setAllAccountsOpened(false);
                }}
                value={searchKeyword}
                type="search"
                placeholder="Search account name or CID"
              />
            </div>
            <Box ml="auto" mr="-7px">
              <Button
                variant="subtle"
                size="sm"
                iconBefore={
                  allAccountsOpenend ? (
                    <Icon.ChevronUp title="Expand All / Collapse All" />
                  ) : (
                    <Icon.ChevronDown title="Expand All / Collapse All" />
                  )
                }
                title="Expand All / Collapse All"
                onClick={() => {
                  setAllAccountsOpened(!allAccountsOpenend);
                  setOpenedAccountIds([]);
                }}
              />
            </Box>
          </ProcedureSearch>
          <Box
            flex="1 0 auto"
            height="1px"
            overflowY="auto"
            overflowX="hidden"
            ref={existingAccountsRef}
          >
            <AccountList style={{ position: 'relative', minHeight: '100%' }}>
              {isGetAccountListLoading && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Loader />
                </div>
              )}
              {existingAccounts &&
                existingAccounts.map((account: linkImportAccountList) => {
                  const isOpnened = opendAccountIds.find(
                    id => id === account.AccountId,
                  );
                  let isAllSelected = false;

                  const filtered =
                    account.ProcedureDetails?.filter((item: any) => {
                      const isExists = selectedExistingProcedures?.some(i =>
                        i?.procedures?.some(
                          (proc: any) => proc.ProcedureId === item.ProcedureId,
                        ),
                      );
                      return !!isExists;
                    }) || [];
                  // to check if all procedures in that particular account is selected
                  isAllSelected = !!(
                    filtered &&
                    filtered.length > 0 &&
                    account &&
                    account?.ProcedureDetails &&
                    filtered?.length === account?.ProcedureDetails.length
                  );

                  return (
                    <Accordion
                      key={account.AccountId}
                      opened={allAccountsOpenend || !!isOpnened}
                      setExpanded={(opened: boolean) => {
                        setOpenedAccountIds(
                          (ids: Array<number>): Array<number> => {
                            if (allAccountsOpenend) {
                              setAllAccountsOpened(false);
                              return [
                                ...existingAccounts
                                  .map((item: any) => item.Id)
                                  .filter(
                                    (id: any) => id !== account.AccountId,
                                  ),
                              ];
                            }
                            if (opened) {
                              return [...ids, account.AccountId];
                            } else {
                              return [
                                ...ids.filter(id => id !== account.AccountId),
                              ];
                            }
                          },
                        );
                      }}
                      toggleButtonVariant="subtle"
                      className="pb-link-listitem"
                      headerContent={
                        <Checkbox
                          handleChange={() =>
                            handleExistingAccountSelect(isAllSelected, account)
                          }
                          checked={allAccountsSelected || isAllSelected}
                          label={account.AccountNameAndNumber}
                          title={account.AccountNameAndNumber}
                          indeterminate={isAnyProcedureChecked(account)}
                          name={`account-${account.AccountId}`}
                          className="pb-cb__link-account"
                        />
                      }
                    >
                      <ProcedureList>
                        {account.ProcedureDetails?.map(
                          (accountProcedure: any) => {
                            const isSelected =
                              selectedExistingProcedures?.some(
                                (procedureList: any) =>
                                  procedureList?.procedures?.some(
                                    (item: any) =>
                                      item.ProcedureId ===
                                      accountProcedure.ProcedureId,
                                  ),
                              ) || false;
                            return (
                              <Flex
                                key={`account.AccountProcedures${accountProcedure.ProcedureId}`}
                              >
                                <Checkbox
                                  handleChange={() =>
                                    handleExistingProcedureSelect(
                                      isSelected,
                                      accountProcedure,
                                      account,
                                    )
                                  }
                                  checked={allAccountsSelected || isSelected}
                                  key={
                                    account.AccountNameAndNumber +
                                    accountProcedure.ProcedureId
                                  }
                                  label={accountProcedure.ProcedureName}
                                  title={accountProcedure.ProcedureName}
                                  name={
                                    account.AccountNameAndNumber +
                                    accountProcedure.ProcedureId
                                  }
                                  className="pb-cb__import_existing-procedure"
                                />
                                <ProcedureAction
                                  noAction
                                  title={
                                    accountProcedure?.IsVisible
                                      ? 'Active'
                                      : 'Hidden'
                                  }
                                >
                                  {accountProcedure?.IsVisible ? (
                                    <Icon.Eye
                                      fill="var(--color-neutral-contrast-05)"
                                      title="Active"
                                    />
                                  ) : (
                                    <Icon.EyeOff
                                      fill="var(--color-neutral-contrast-05)"
                                      title="Hidden"
                                    />
                                  )}
                                </ProcedureAction>
                              </Flex>
                            );
                          },
                        )}
                      </ProcedureList>
                    </Accordion>
                  );
                })}
            </AccountList>
          </Box>
        </Flex>
      ),
    },
    {
      id: 'archivedProcedures',
      label: 'Archived',
      content: (
        <Flex flexDirection="column" height="100%">
          <ProcedureSearch>
            <Checkbox
              handleChange={() => handleArchiveSelectAll()}
              checked={allArchivedSelected}
              label=" "
              name="selectAllArchived"
            />
            <div className="search-input">
              <Icon.Search />
              <input
                value={archiveSearch}
                onChange={e => setArchiveSearch(e.target.value)}
                type="search"
                placeholder="Search procedure name or Id"
              />
            </div>
          </ProcedureSearch>
          <Box flex="1 0 auto" height="1px" overflow="auto">
            <ProcedureList>
              {archivedProcedures &&
                archivedProcedures.map((item: any) => {
                  const isSelected = selectedArchivedProcedures?.find(
                    i => i.ProcedureId === item.ProcedureId,
                  );

                  return (
                    <Flex key={`archivedProcedures-${item.ProcedureId}`}>
                      <Checkbox
                        handleChange={() => {
                          handleArchiveProcedureSelect(isSelected, item);
                        }}
                        checked={!!isSelected}
                        key={item.Procedure.Id}
                        label={item.Procedure.Name}
                        title={item.Procedure.Name}
                        name={`procedure-${item.Procedure.Id}`}
                        className="pb-cb__import-procedure"
                      />
                      <ProcedureAction
                        title="Remove procedure"
                        onClick={() => {
                          confirmAlert({
                            message:
                              'Are you sure you want to delete the procedure(s)?',
                            buttons: [
                              {
                                label: 'Yes',
                                onClick: () => {
                                  handleDeleteProcedure(item);
                                },
                                className: 'pr-btn-primary',
                              },
                              {
                                label: 'No',
                                onClick: () => {
                                  //
                                },
                              },
                            ],
                          });
                        }}
                      >
                        <Icon.Delete
                          title="Remove procedure"
                          fill="var(--color-neutral-contrast-06)"
                        />
                      </ProcedureAction>
                    </Flex>
                  );
                })}
            </ProcedureList>
          </Box>
        </Flex>
      ),
    },
  ];

  //Import Panel
  const panelActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          togglePanel && togglePanel();
          resetSelectedProcedures();
          clearSearches();
        }}
      >
        Cancel
      </Button>

      <Tooltip
        animation="fade"
        // title="Procedures will be imported as invisible. Don't forget to mark them as visible when you're done!"
        position="top"
        arrow={true}
        arrowSize="small"
        html={
          <div style={{ textAlign: 'left', width: '240px' }}>
            Procedures will be imported as invisible. Don&apos;t forget to mark
            them as visible when you&apos;re done!
          </div>
        }
      >
        <Button
          disabled={(() => {
            const allSelections = getAllSelectedProcedures();
            if (allSelections.length > 0) return false;
            return true;
          })()}
          onClick={() => {
            if (isDuplicateProcedureNameExist()) {
              showErrorToast({
                message:
                  'Duplicate procedure names exist in the selection. Please resolve them before proceeding.',
              });
              return;
            }
            setInvisibleState(true);
            const allSelections = getAllSelectedProcedures();
            if (allSelections.length > 0) {
              importProcedures(
                {
                  accountId,
                  checkedProcedures: allSelections,
                  urlPayload: 'ImportAsInvisible ',
                },
                {
                  onSuccess: (result: ResultModal) => {
                    if (
                      result &&
                      result.Data.Name !== 'Imported Successfully'
                    ) {
                      setDuplicateProcedures(result.Data);
                      setIsDuplicateModal(true);
                      return;
                    }
                    showSuccessToast({
                      message: `Procedure${
                        allSelections.length > 1 ? '(s)' : ''
                      } Imported Successfully`,
                    });
                    resetSelectedProcedures();
                    togglePanel();
                  },
                  onError: (error: ErrorModal) => {
                    showErrorToast({
                      message: error.Errors[0].Message,
                    });
                  },
                },
              );
            } else {
              showWarningToast({
                message: 'Please select atleast one procedure to proceed.',
              });
            }
          }}
          variant="primary"
        >
          Import as Invisible
        </Button>
      </Tooltip>

      <Tooltip
        animation="fade"
        position="top"
        arrow={true}
        arrowSize="small"
        html={
          <div style={{ textAlign: 'left', width: '220px' }}>
            Procedures will be accessible to users right away.
          </div>
        }
      >
        <Button
          variant="danger"
          disabled={(() => {
            const allSelections = getAllSelectedProcedures();
            if (allSelections.length > 0) return false;
            return true;
          })()}
          onClick={() => {
            if (isDuplicateProcedureNameExist()) {
              showErrorToast({
                message:
                  'Duplicate procedure names exist in the selection. Please resolve them before proceeding.',
              });
              return;
            }
            setInvisibleState(false);
            const allSelections = getAllSelectedProcedures();
            if (allSelections.length > 0) {
              importProcedures(
                {
                  accountId,
                  checkedProcedures: allSelections,
                  urlPayload: 'ImportAndPublish',
                },
                {
                  onSuccess: (result: ResultModal) => {
                    if (
                      result &&
                      result.Data.Name !== 'Imported Successfully'
                    ) {
                      setDuplicateProcedures(result.Data);
                      setIsDuplicateModal(true);
                      return;
                    }
                    showSuccessToast({
                      message: `Procedure${
                        allSelections.length > 1 ? '(s)' : ''
                      } Imported Successfully`,
                    });
                    resetSelectedProcedures();
                    togglePanel();
                  },
                  onError: (error: ErrorModal) => {
                    showErrorToast({
                      message: error.Errors[0].Message,
                    });
                  },
                },
              );
            } else {
              showWarningToast({
                message: 'Please select atleast one procedure to proceed.',
              });
            }
          }}
        >
          Import &amp; Publish
        </Button>
      </Tooltip>
    </>
  );

  //Import duplication panel
  const duplicatePanelActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          resetDuplicatePanel();
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={
          (selectedSkips && selectedSkips.length > 0) ||
          (selectedDuplicates && selectedDuplicates.length > 0)
            ? false
            : true
        }
        onClick={() => {
          if (selectedDuplicates && selectedDuplicates.length > 0) {
            duplicateImportedProcedure(
              {
                accountId,
                selectedDuplicates,
                visibility: invisibleState
                  ? 'ImportAsInvisible'
                  : 'ImportAndPublish',
              },
              {
                onSuccess: () => {
                  showSuccessToast({
                    message: `Procedure${
                      selectedDuplicates.length > 1 ? '(s)' : ''
                    } Imported Successfully`,
                  });
                  resetDuplicatePanel();
                  resetSelectedProcedures();
                  togglePanel();
                },
                onError: (error: ErrorModal) => {
                  showErrorToast({
                    message: error.Errors[0].Message,
                  });
                },
              },
            );
          } else {
            showSuccessToast({
              message: 'Updated Successfully',
            });
            resetDuplicatePanel();
            resetSelectedProcedures();
            togglePanel();
          }
        }}
      >
        Continue
      </Button>
    </>
  );

  return (
    <>
      <SlidePanel
        open={isVisible}
        onClose={() => {
          togglePanel && togglePanel();
          resetSelectedProcedures();
          clearSearches();
        }}
        size="lg"
        title="Import Procedure"
        actions={panelActions()}
      >
        <Flex flexDirection="column" style={{ height: '100%' }}>
          <Flex flex="1 1 auto">
            <Card
              width="345px"
              flexDirection="column"
              style={{ display: 'flex' }}
            >
              <CardHeader p={3} bg="var(--color-neutral-contrast-02)">
                <Text
                  as="h4"
                  fontWeight="medium"
                  lineHeight={1}
                  color="text.body"
                >
                  Procedures
                </Text>
              </CardHeader>
              <Tabs tabs={importProcedureTabs} importProcedure />
            </Card>

            <Card
              flex="1"
              ml={3}
              position="relative"
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <CardHeader p={3} bg="var(--color-neutral-contrast-02)">
                <Text
                  as="h4"
                  fontWeight="medium"
                  lineHeight={1}
                  color="text.body"
                >
                  {`${getAllSelectedProcedures().length} procedures selected`}
                </Text>
              </CardHeader>
              <StickyListWrap overflowY="auto" flex="1 0 auto" height="1px">
                <StickyList>
                  {globalProceduresSelectedList.length > 0 && (
                    <>
                      <StickyListTitle borderTop="none">
                        Global Procedures
                      </StickyListTitle>
                      {globalProceduresSelectedList.map(item => (
                        <StickyListItem
                          key={`Global_${item?.AccountId}_${item?.ProcedureId}`}
                        >
                          {item?.ProcedureName}
                          <Button
                            variant="subtle"
                            size="sm"
                            title="Preview procedure"
                            onClick={() => {
                              if (item)
                                setShowPreview({
                                  accountId: item.AccountId,
                                  procedureId: item.ProcedureId,
                                });
                            }}
                          >
                            <Icon.Document title="Preview procedure" />
                          </Button>
                          <Button
                            variant="subtle"
                            size="sm"
                            title="Remove procedure"
                            onClick={() =>
                              handleRemoveGlobalProcedure(item?.ProcedureId)
                            }
                          >
                            <Icon.X title="Remove procedure" />
                          </Button>
                        </StickyListItem>
                      ))}
                    </>
                  )}
                </StickyList>
                <StickyList>
                  {selectedAccounts.length > 0 && (
                    <>
                      {selectedAccounts?.map(
                        ({ accountId, accountNameAndNumber, procedures }) => (
                          <>
                            <StickyListTitle>
                              {accountNameAndNumber}
                            </StickyListTitle>
                            {procedures.map((item: any) => (
                              <>
                                <StickyListItem key={item?.ProcedureId}>
                                  {item?.ProcedureName}
                                  <Button
                                    variant="subtle"
                                    size="sm"
                                    title="Preview procedure"
                                    onClick={() => {
                                      if (item)
                                        setShowPreview({
                                          accountId: accountId,
                                          procedureId: item.ProcedureId,
                                        });
                                    }}
                                  >
                                    <Icon.Document title="Preview procedure" />
                                  </Button>
                                  <Button
                                    variant="subtle"
                                    size="sm"
                                    title="Remove procedure"
                                    onClick={() =>
                                      handleRemoveExistingAccountProcedure(item)
                                    }
                                  >
                                    <Icon.X title="Remove procedure" />
                                  </Button>
                                </StickyListItem>
                              </>
                            ))}
                          </>
                        ),
                      )}
                    </>
                  )}
                </StickyList>
                <StickyList>
                  {archiveProceduresSelectedList.length > 0 && (
                    //LIST ON THE RIGHT SIDE
                    <>
                      <StickyListTitle>Archived procedures</StickyListTitle>
                      {archiveProceduresSelectedList.map(item => (
                        <StickyListItem key={item?.ProcedureId}>
                          <Stack direction="y" gap={1}>
                            <Text as="span">{item?.ProcedureName}</Text>
                            <Flex alignItems="center">
                              <Text
                                as="small"
                                fontSize="0.75rem"
                                fontWeight="medium"
                                color="var(--color-neutral-contrast-05)"
                              >{`Archived on ${getArchivedTime(
                                item?.LastUpdatedOn,
                              )} by ${item?.LastUpdatedBy}`}</Text>
                            </Flex>
                          </Stack>
                          <Button
                            variant="subtle"
                            size="sm"
                            title="Preview procedure"
                            onClick={() => {
                              if (item)
                                setShowPreview({
                                  accountId: item.AccountId,
                                  procedureId: item.ProcedureId,
                                });
                            }}
                          >
                            <Icon.Document title="Preview procedure" />
                          </Button>
                          <Button
                            variant="subtle"
                            size="sm"
                            title="Remove procedure"
                            onClick={() =>
                              handleRemoveArchiveProcedure(item?.ProcedureId)
                            }
                          >
                            <Icon.X title="Remove procedure" />
                          </Button>
                        </StickyListItem>
                        //LIST ON THE RIGHT SIDE
                      ))}
                    </>
                  )}
                </StickyList>
              </StickyListWrap>
            </Card>
          </Flex>
          <PreviewPanel
            previewProcedure={!!showPreview}
            closePreviewProcedure={() => {
              setShowPreview(undefined);
            }}
            selectedIds={showPreview}
          />
          {/* Linked Accounts */}
          <SlidePanel
            title={linkedAccounts && linkedAccounts?.Name}
            size="sm"
            open={!!linkedAccounts}
            onClose={() => setLinkedAccounts(undefined)}
          >
            <Flex flexDirection="column" height="100%">
              <Box>
                <SearchInput
                  value={linkedAccountsSearch}
                  handleChange={(value: any) => setLinkedAccountsSearch(value)}
                  placeholder="Search linked accounts"
                  mb={3}
                />
              </Box>
              <Stack
                direction="y"
                gap={3}
                flex="1 0 auto"
                height="1px"
                overflowY="auto"
              >
                {isLinkedAccountsForGlobalProcedureLoading && <Loader />}
                {linkedAccountsArray &&
                  linkedAccountsArray.map((item: any) => (
                    <Text key={item}>{item}</Text>
                  ))}
              </Stack>
            </Flex>
          </SlidePanel>
          {isLoading && <Loader />}
        </Flex>
      </SlidePanel>
      <SlidePanel
        open={isDuplicateModal}
        onClose={() => {
          setIsDuplicateModal && setIsDuplicateModal(!isDuplicateModal);
          setInvisibleState(false);
        }}
        size="lg"
        title="Import Procedure"
        actions={duplicatePanelActions()}
      >
        <Flex flexDirection="column" height="100%">
          <Stack direction="y" gap={3}>
            <Alert variant="warning" icon={<Icon.Warning />}>
              Below procedures cannot be imported as the procedure name already
              exists
            </Alert>
            <DataTableWrapper>
              <DataTable className="choice-qn-dependency without-search">
                <thead>
                  <tr>
                    <th>Procedures</th>
                    <th>
                      <Checkbox
                        label="Duplicate All"
                        name="selectAllDuplicate"
                        checked={isAllDuplicateChecked()}
                        handleChange={() => handleSelectAllDuplicates()}
                        disabled={
                          selectedSkips && selectedSkips.length > 0
                            ? true
                            : false
                        }
                      />
                    </th>
                    <th>
                      <Checkbox
                        label="Skip All"
                        name="selectAllSkip"
                        checked={isAllSkipChecked()}
                        handleChange={() => handleSelectAllSkips()}
                        disabled={
                          selectedDuplicates && selectedDuplicates.length > 0
                            ? true
                            : false
                        }
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {duplicateProcedures &&
                    duplicateProcedures.map((procedure: any) => {
                      return (
                        <tr key={procedure.ProcedureId}>
                          <td>{`${procedure.ProcedureName} (${
                            procedure.IsGlobal
                              ? 'Global'
                              : procedure.AccountName
                          })`}</td>
                          <td>
                            <Checkbox
                              label="Duplicate"
                              name={`duplicate-${procedure.ProcedureId}`}
                              checked={procedure.IsCopy ? true : false}
                              handleChange={() => handleDuplicate(procedure)}
                              disabled={procedure.IsSkip ? true : false}
                            />
                          </td>
                          <td>
                            <Checkbox
                              label="Skip"
                              name={`skip-${procedure.ProcedureId}`}
                              checked={procedure.IsSkip ? true : false}
                              handleChange={() => handleSkip(procedure)}
                              disabled={procedure.IsCopy ? true : false}
                            />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </DataTable>
            </DataTableWrapper>
          </Stack>
        </Flex>
        {isDuplicateImportsLoading && <Loader />}
      </SlidePanel>
    </>
  );
}
